import { useRef, useState } from "react";
import { Plus, X } from "lucide-react";
import { Button } from "@/shared/components/shadcn/Button";
import { Input } from "@/shared/components/shadcn/Input";
import { Switch } from "@/shared/components/shadcn/Switch";
import { Card } from "@/shared/components/ui/Card";
import { ConfirmationDialog } from "@/shared/components/ui/ConfirmationDialog";
import { ErrorState } from "@/shared/components/ui/ErrorState";
import { FieldGroup } from "@/shared/components/ui/FieldGroup";
import { FormField } from "@/shared/components/ui/FormField";
import { FormPage } from "@/shared/components/ui/FormPage";
import { PageHeader } from "@/shared/components/ui/PageHeader";
import { Section } from "@/shared/components/ui/Section";
import { SectionAction } from "@/shared/components/ui/SectionAction";
import { useAppNavigation } from "@/shared/hooks/useAppNavigation";
import { isValidTimezone } from "@/shared/utils/timezones";
import { useCreateAvailability, useUpdateAvailability } from "../hooks/useAvailabilities";
import type { Availability, Schedule, TimeSlot } from "../types/availabilityTypes";
import { userAction } from "@/core/telemetry/userAction";

const DAYS = [
  { key: "monday", label: "Monday" },
  { key: "tuesday", label: "Tuesday" },
  { key: "wednesday", label: "Wednesday" },
  { key: "thursday", label: "Thursday" },
  { key: "friday", label: "Friday" },
  { key: "saturday", label: "Saturday" },
  { key: "sunday", label: "Sunday" },
] as const;
type DayName = (typeof DAYS)[number]["key"];

const emptySchedule = (): Schedule => ({
  monday: [], tuesday: [], wednesday: [], thursday: [],
  friday: [], saturday: [], sunday: [],
});
const validTime = (value: string) => /^([01]\d|2[0-3]):[0-5]\d$/.test(value);
const inputId = (day: DayName, index: number, field: keyof TimeSlot) =>
  `${day}-${index}-${field}`;
const focusControl = (id: string) =>
  requestAnimationFrame(() => document.getElementById(id)?.focus());

interface WorkingHoursFormProps {
  userId: string;
  businessId: string;
  locationId: string;
  businessTimezone: string;
  initialAvailability?: Availability;
  refreshError?: string;
  onRetry: () => void;
}

export function WorkingHoursForm({
  userId, businessId, locationId, businessTimezone, initialAvailability,
  refreshError, onRetry,
}: WorkingHoursFormProps) {
  const { goToProfile } = useAppNavigation();
  // Initialize once per business/location; background refetches must not replace a draft.
  const [schedule, setSchedule] = useState<Schedule>(
    () => structuredClone(initialAvailability?.schedule ?? emptySchedule()),
  );
  const [enabled, setEnabled] = useState(() =>
    Object.fromEntries(DAYS.map(({ key }) => [key, schedule[key].length > 0])) as Record<DayName, boolean>,
  );
  const [availabilityId, setAvailabilityId] = useState(initialAvailability?.id);
  const identityChanged = !!initialAvailability && initialAvailability.id !== availabilityId;
  const [replaceSavedHours, setReplaceSavedHours] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [notice, setNotice] = useState("");
  const [saveError, setSaveError] = useState("");
  const [removal, setRemoval] = useState<{ day: DayName; index: number } | null>(null);
  const create = useCreateAvailability();
  const update = useUpdateAvailability(availabilityId ?? "");
  const submitting = useRef(false);
  const isSaving = create.isPending || update.isPending;

  function clearFeedback() {
    setErrors({});
    setNotice("");
    setSaveError("");
  }

  function toggleDay(day: DayName, checked: boolean) {
    clearFeedback();
    setEnabled((previous) => ({ ...previous, [day]: checked }));
    if (checked && schedule[day].length === 0) {
      setSchedule((previous) => ({ ...previous,       [day]: [{ start: "09:00", end: "17:00" }] }));
    }
  }

  function changeTime(day: DayName, index: number, field: keyof TimeSlot, value: string) {
    clearFeedback();
    setSchedule((previous) => ({
      ...previous,
      [day]: previous[day].map((slot, slotIndex) =>
        slotIndex === index ? { ...slot, [field]: value } : slot),
    }));
  }

  function removeInterval() {
    if (!removal) return;
    const { day, index } = removal;
    const remaining = schedule[day].filter((_, slotIndex) => slotIndex !== index);
    clearFeedback();
    setSchedule((previous) => ({ ...previous, [day]: remaining }));
    if (remaining.length === 0) setEnabled((previous) => ({ ...previous, [day]: false }));
    setRemoval(null);
    setNotice("Interval removed from the draft. Save to apply your changes.");
    focusControl(remaining.length ? `${day}-add` : `${day}-enabled`);
  }

  async function save() {
    if (submitting.current || identityChanged) return;
    clearFeedback();
    const failures: Record<string, string> = {};
    const nextSchedule = emptySchedule();
    for (const { key: day, label } of DAYS) {
      if (!enabled[day]) continue;
      const ordered = schedule[day]
        .map((slot, index) => ({ ...slot, index }))
        .sort((a, b) => a.start.localeCompare(b.start));
      for (const slot of ordered) {
        for (const field of ["start", "end"] as const) {
          if (!validTime(slot[field])) {
            failures[inputId(day, slot.index, field)] = `${label}: enter a valid ${field} time.`;
          }
        }
        if (validTime(slot.start) && validTime(slot.end) && slot.end <= slot.start) {
          failures[inputId(day, slot.index, "end")] = "End must be after start on the same day.";
        }
      }
      for (let index = 1; index < ordered.length; index++) {
        if (ordered[index].start < ordered[index - 1].end) {
          failures[inputId(day, ordered[index].index, "start")] = `${label}: intervals must not overlap.`;
        }
      }
      nextSchedule[day] = ordered.map(({ start, end }) => ({ start, end }));
    }
    if (!isValidTimezone(businessTimezone)) failures.timezone = "Choose a valid business timezone in your profile.";
    if (Object.keys(failures).length) {
      setErrors(failures);
      focusControl(Object.keys(failures)[0]);
      return;
    }
    submitting.current = true;
    try {
      const changes = { schedule: nextSchedule, isActive: true };
      const saved = availabilityId
        ? await update.mutateAsync(changes)
        : await create.mutateAsync({ userId, businessId, locationId, timezone: businessTimezone, ...changes });
      // Keep the returned identity immediately, even if list invalidation fails or lags.
      setAvailabilityId(saved.id);
      setSchedule((previous) => Object.fromEntries(DAYS.map(({ key }) => [
        key, enabled[key] ? saved.schedule[key] : previous[key],
      ])) as Schedule);
      setNotice("Working hours saved.");
    } catch (error) {
      setSaveError(error instanceof Error ? error.message : "Couldn't save working hours. Try again.");
    } finally {
      submitting.current = false;
    }
  }

  const removingSlot = removal ? schedule[removal.day][removal.index] : undefined;

  return (
    <FormPage
      header={<PageHeader title="Working hours" />}
      onSubmit={(event) => { event.preventDefault(); void save(); }}
      contentClassName="space-y-4"
      actions={<Button type="submit" {...userAction("availability.form_submit")} disabled={isSaving || identityChanged}>{isSaving ? "Saving..." : "Save"}</Button>}
    >
      {refreshError && <ErrorState title="Couldn't refresh working hours" message={`${refreshError} Your draft is unchanged.`} onRetry={onRetry} />}
      {identityChanged && initialAvailability && (
        <Section title="Working hours were created elsewhere">
          <Card className="space-y-3">
            <p role="alert" className="text-sm text-muted-foreground">Your draft has been kept. Load the saved schedule, or choose to replace it with your draft before saving.</p>
            <div className="flex flex-wrap gap-2">
              <Button variant="outline" disabled={isSaving} onClick={() => {
                const savedSchedule = structuredClone(initialAvailability.schedule);
                setSchedule(savedSchedule);
                setEnabled(Object.fromEntries(DAYS.map(({ key }) => [key, savedSchedule[key].length > 0])) as Record<DayName, boolean>);
                setAvailabilityId(initialAvailability.id);
                clearFeedback();
                setNotice("Saved working hours loaded.");
              }}>Use saved hours</Button>
              <Button variant="outline" disabled={isSaving} onClick={() => setReplaceSavedHours(true)}>Keep my draft</Button>
            </div>
          </Card>
        </Section>
      )}
      <Section title="Weekly availability" action={<SectionAction id="timezone" onClick={goToProfile}>Business settings</SectionAction>}>
        <p className="text-sm text-muted-foreground">Choose your regular working days and times. Saving hours does not move existing appointments.</p>
        {errors.timezone && <ErrorState message={errors.timezone} />}
        {DAYS.map(({ key: day, label }) => (
          <FieldGroup key={day} disabled={isSaving} aria-label={label}>
            <div className="flex min-h-11 items-center justify-between gap-3">
              <p className="text-[15px] font-semibold">{label}</p>
              <Switch
                id={`${day}-enabled`}
                aria-label={`${label} available`}
                checked={enabled[day]}
                onCheckedChange={(checked) => toggleDay(day, checked)}
                size="lg"
              />
            </div>
            {enabled[day] && (
              <div role="group" aria-label={`${label} intervals`} className="space-y-[18px]">
                {schedule[day].map((slot, index) => (
                  <div key={index} className="grid grid-cols-[minmax(0,1fr)_44px] items-start gap-2">
                    <div className="grid min-w-0 grid-cols-2 gap-2">
                      {(["start", "end"] as const).map((field) => (
                        <FormField
                          key={field}
                          id={inputId(day, index, field)}
                          label={`${field === "start" ? "Start" : "End"} ${index + 1}`}
                          required
                          error={errors[inputId(day, index, field)]}
                        >
                          <Input
                            type="time"
                            step={60}
                            value={slot[field]}
                            aria-label={`${label} interval ${index + 1} ${field}`}
                            className="min-w-0 px-2 [&::-webkit-calendar-picker-indicator]:hidden"
                            onChange={(event) => changeTime(day, index, field, event.target.value)}
                          />
                        </FormField>
                      ))}
                    </div>
                    <Button
                      variant="ghost"
                      size="icon-sm"
                      className="mt-7"
                      aria-label={`Remove ${label} interval ${index + 1}`}
                      onClick={() => setRemoval({ day, index })}
                    >
                      <X aria-hidden="true" />
                    </Button>
                  </div>
                ))}
                <Button
                  id={`${day}-add`}
                  variant="quiet"
                  aria-label={`Add ${label} availability`}
                  onClick={() => {
                    clearFeedback();
                    setSchedule((previous) => ({
                      ...previous, [day]: [...previous[day], { start: "", end: "" }],
                    }));
                    focusControl(inputId(day, schedule[day].length, "start"));
                  }}
                >
                  <Plus aria-hidden="true" /> Add availability
                </Button>
              </div>
            )}
          </FieldGroup>
        ))}
      </Section>
      {Object.keys(errors).length > 0 && <ErrorState title="Check your hours" message="Correct the highlighted fields before saving. Your draft has been kept." />}
      {saveError && <ErrorState title="Couldn't save working hours" message={`${saveError} Your draft has been kept. Refresh saved hours before trying Save again.`} onRetry={onRetry} />}
      <p role="status" aria-live="polite" className="text-sm text-primary">{notice}</p>
      <ConfirmationDialog
        open={replaceSavedHours && identityChanged}
        onOpenChange={setReplaceSavedHours}
        title="Replace the saved working hours?"
        description="Your draft will be kept. Saving next will replace the schedule created elsewhere for this business and location."
        confirmLabel="Keep my draft"
        dismissLabel="Keep reviewing"
        onConfirm={() => {
          if (!initialAvailability) return;
          setAvailabilityId(initialAvailability.id);
          setReplaceSavedHours(false);
          clearFeedback();
          setNotice("Your draft is ready. Save to replace the existing working hours.");
        }}
      />
      <ConfirmationDialog
        open={removal !== null}
        onOpenChange={(open) => { if (!open) setRemoval(null); }}
        title="Remove this interval?"
        description={`${DAYS.find(({ key }) => key === removal?.day)?.label ?? ""}, ${removingSlot?.start || "unset"} to ${removingSlot?.end || "unset"}. This changes the draft only. Save to apply it.`}
        confirmLabel="Remove interval"
        dismissLabel="Keep interval"
        destructive
        onConfirm={removeInterval}
      />
    </FormPage>
  );
}
